import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCreditCard } from '@fortawesome/pro-solid-svg-icons/faCreditCard';
import { faFileCheck } from '@fortawesome/pro-solid-svg-icons/faFileCheck';
import { faBuilding } from '@fortawesome/pro-solid-svg-icons/faBuilding';
import { faSyringe } from '@fortawesome/pro-solid-svg-icons/faSyringe';
import { faHandHoldingHeart } from '@fortawesome/pro-solid-svg-icons/faHandHoldingHeart';
import { faRepeat } from '@fortawesome/pro-solid-svg-icons/faRepeat';
import { faUserGroup } from '@fortawesome/pro-solid-svg-icons/faUserGroup';
import { faCommentDots } from '@fortawesome/pro-solid-svg-icons/faCommentDots';
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons/faBadgeCheck';
import { faClock } from '@fortawesome/pro-solid-svg-icons/faClock';
import { faStar } from '@fortawesome/pro-solid-svg-icons/faStar';
import { faShieldCheck } from '@fortawesome/pro-solid-svg-icons/faShieldCheck';
import { faGraduationCap } from '@fortawesome/pro-solid-svg-icons/faGraduationCap';
import { faPeopleRoof } from '@fortawesome/pro-solid-svg-icons/faPeopleRoof';
import { faPeopleGroup } from '@fortawesome/pro-solid-svg-icons/faPeopleGroup';

import { SearchCardBadge, SearchCardConnection, SelfBadge } from './utils';

interface Props {
  selfBadge: SelfBadge | null;
  connections: SearchCardConnection[];
  groupConnections: SearchCardConnection[];
  badges: SearchCardBadge[];
  badgeDisplayCount: number;
  overflowCount: number;
  userPictureUrl?: string;
}

const ASSET_ROOT = 'https://assets.urbansitter.com/static/img/badges';
const SOCIAL_FRIENDS_ASSET = `${ASSET_ROOT}/social-friends.png`;
const LOGO_PRIMARY_ASSET = `${ASSET_ROOT}/logo-primary.svg`;

const BADGE_ICONS: Record<string, IconDefinition> = {
  acceptsCreditCards: faCreditCard,
  backgroundCheck: faFileCheck,
  cc: faCreditCard,
  corporate: faBuilding,
  covidBadge: faSyringe,
  cpr: faHandHoldingHeart,
  family: faRepeat,
  friend: faUserGroup,
  messageResponse: faCommentDots,
  reliable: faBadgeCheck,
  repeatFamilies: faRepeat,
  repeated_sitter: faRepeat,
  response: faClock,
  responseTime: faClock,
  review: faStar,
  reviewCount: faStar,
  trustedBy: faShieldCheck,
};

const GROUP_CONNECTION_ICONS: Record<'school' | 'affiliation' | 'localGroup', IconDefinition> = {
  school: faGraduationCap,
  affiliation: faPeopleRoof,
  localGroup: faPeopleGroup,
};

type BadgeVisual =
  | { kind: 'icon'; icon: IconDefinition }
  | { kind: 'image'; src: string };

const LOGO_VISUAL: BadgeVisual = { kind: 'image', src: LOGO_PRIMARY_ASSET };

const resolveBadgeVisual = (badge: SearchCardBadge): BadgeVisual => {
  if (badge.type === 'sitterMember' || badge.type === 'member-since') return LOGO_VISUAL;
  if (badge.type === 'event') {
    const icon = badge.icon ?? '';
    if (icon.includes('badge-cpr')) return { kind: 'icon', icon: faHandHoldingHeart };
    if (icon.includes('badge-trusted-by')) return { kind: 'icon', icon: faShieldCheck };
    return LOGO_VISUAL;
  }
  if (badge.type && BADGE_ICONS[badge.type]) return { kind: 'icon', icon: BADGE_ICONS[badge.type] };
  return LOGO_VISUAL;
};

const CIRCLE_CLASS = 'inline-flex items-center justify-center mr-2 cursor-default bg-gray-300 text-sapphire';
const CIRCLE_STYLE: React.CSSProperties = { width: 40, height: 40, borderRadius: 20 };

export const BadgeIcon = ({ badge }: { badge: SearchCardBadge }): JSX.Element => {
  const tooltip = badge.description || badge.label;
  const visual = resolveBadgeVisual(badge);
  return (
    <span title={tooltip} aria-label={tooltip} className={CIRCLE_CLASS} style={CIRCLE_STYLE}>
      {visual.kind === 'icon' ? (
        <FontAwesomeIcon icon={visual.icon} className='w-5 h-5' />
      ) : (
        <img alt='' src={visual.src} className='w-5 h-5' />
      )}
    </span>
  );
};

const DirectConnectionIcon = ({
  connection,
}: {
  connection: SearchCardConnection;
}): JSX.Element => {
  const isMultiple = connection.variant === 'multipleFriends';
  return (
    <span
      title={connection.label}
      aria-label={connection.label}
      className='inline-flex items-center justify-center mr-2 cursor-default relative bg-no-repeat bg-center'
      style={{
        width: 40,
        height: 40,
        backgroundImage: isMultiple ? `url('${SOCIAL_FRIENDS_ASSET}')` : undefined,
        backgroundSize: isMultiple ? '40px 40px' : undefined,
      }}
    >
      {connection.icon && (
        <img
          alt=''
          src={connection.icon}
          className={
            isMultiple
              ? 'absolute top-[4px] left-[3px] w-[37px] h-[37px] rounded-full object-cover'
              : 'w-10 h-10 rounded-full object-cover'
          }
        />
      )}
    </span>
  );
};

const GroupConnectionIcon = ({
  connection,
}: {
  connection: SearchCardConnection;
}): JSX.Element => {
  if (connection.variant === 'company') {
    return (
      <span
        title={connection.label}
        aria-label={connection.label}
        className='inline-flex items-center justify-center mr-2 cursor-default'
        style={{ width: 40, height: 40 }}
      >
        {connection.icon && (
          <img
            alt=''
            src={connection.icon}
            className='w-10 h-10 rounded-full object-cover'
          />
        )}
      </span>
    );
  }

  const variant = (connection.variant ?? 'localGroup') as 'school' | 'affiliation' | 'localGroup';
  const icon = GROUP_CONNECTION_ICONS[variant] ?? GROUP_CONNECTION_ICONS.localGroup;

  return (
    <span
      title={connection.label}
      aria-label={connection.label}
      className={CIRCLE_CLASS}
      style={CIRCLE_STYLE}
    >
      <FontAwesomeIcon icon={icon} className='w-5 h-5' />
    </span>
  );
};

export const BadgesRow = ({
  selfBadge,
  connections,
  groupConnections,
  badges,
  badgeDisplayCount,
  overflowCount,
  userPictureUrl,
}: Props): JSX.Element | null => {
  const visibleBadges = badges.slice(0, badgeDisplayCount);
  const hasContent =
    Boolean(selfBadge) ||
    connections.length > 0 ||
    groupConnections.length > 0 ||
    visibleBadges.length > 0 ||
    overflowCount > 0;
  if (!hasContent) return null;

  return (
    <div
      className='flex flex-row flex-wrap items-center mt-3'
      onClick={(event): void => event.stopPropagation()}
      onKeyDown={(event): void => event.stopPropagation()}
      role='presentation'
    >
      {selfBadge && userPictureUrl && (
        <span
          title={selfBadge.label}
          aria-label={selfBadge.label}
          className='inline-flex items-center justify-center mr-2 cursor-default'
          style={{ width: 40, height: 40 }}
        >
          <img
            alt={selfBadge.label}
            src={userPictureUrl}
            className='w-10 h-10 rounded-full object-cover'
          />
        </span>
      )}
      {connections.map((connection) => (
        <DirectConnectionIcon
          key={`connection-${connection.label}`}
          connection={connection}
        />
      ))}
      {visibleBadges.map((badge) => (
        <BadgeIcon key={`badge-${badge.type ?? badge.label ?? ''}`} badge={badge} />
      ))}
      {groupConnections.map((connection) => (
        <GroupConnectionIcon
          key={`group-${connection.label}`}
          connection={connection}
        />
      ))}
      {overflowCount > 0 && (
        <span
          aria-label={`${overflowCount} more`}
          className='inline-flex items-center justify-center bg-sapphire text-white font-semibold mr-2'
          style={{ width: 40, height: 40, borderRadius: 20 }}
        >
          +{overflowCount}
        </span>
      )}
    </div>
  );
};
